import dayjs from 'dayjs'

/**
 * 秒數轉時間物件
 */
export const getTimeDataBySeconds = (
  sec: number | string, // 總秒數
  normal = false, // 是否要正常時間化，hh 不會超過 24, mm ss 不會超過 60
): {
  value: number // 剩餘秒數
  DDHHmmss: string // 格式化字串 00:00:00:00 依照 normal 選項決定突破值
  HHmmss: string // 格式化字串 00:00:00 依照 normal 選項決定突破值
  mmss: string // 格式化字串 00:00 依照 normal 選項決定突破值
  day: number // 日
  hour: number // 時
  minute: number // 分
  second: number // 秒
  DD: string // 補 0
  HH: string // 補 0
  mm: string // 補 0
  ss: string // 補 0
} => {
  const value = Math.max(0, Number(sec))
  const day = Math.floor(value / 86400)
  const hour = Math.floor((value % 86400) / 3600)
  const minute = Math.floor((value % 3600) / 60)
  const second = Math.floor(value % 60)
  const DD = String(day).padStart(2, '0')
  const HH = String(hour).padStart(2, '0')
  const mm = String(minute).padStart(2, '0')
  const ss = String(second).padStart(2, '0')
  return {
    value,
    DDHHmmss: `${DD}:${HH}:${mm}:${ss}`,
    HHmmss: normal ? `${HH}:${mm}:${ss}` : `${String(hour + day * 24).padStart(2, '0')}:${mm}:${ss}`,
    mmss: normal ? `${mm}:${ss}` : `${String(minute + (hour + day * 24) * 60).padStart(2, '0')}:${ss}`,
    day,
    hour,
    minute,
    second,
    DD,
    HH,
    mm,
    ss,
  }
}

/**
 * 毫秒數轉時間物件
 */
export const getTimeData = (millisecond: number) => {
  return getTimeDataBySeconds(millisecond * 0.001)
}

/**
 * 格式化日期時間為 YYYY-MM-DD HH:mm:ss 格式
 * @param dateTime - 日期時間（可以是 ISO 字串、時間戳或 Date 物件）
 * @returns 格式化後的字串，例如：2025-10-01 15:42:17
 */
export const formatDateTime = (dateTime: string | number | Date): string => {
  if (!dateTime) return ''

  const date = typeof dateTime === 'string' || typeof dateTime === 'number' ? new Date(dateTime) : dateTime

  // 檢查是否為有效日期
  if (isNaN(date.getTime())) return ''

  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  const hours = String(date.getHours()).padStart(2, '0')
  const minutes = String(date.getMinutes()).padStart(2, '0')
  const seconds = String(date.getSeconds()).padStart(2, '0')

  return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`
}

type TranslateFunction = (...args: any[]) => any

/**
 * n 天前
 */
export const fromNow = (t: TranslateFunction, timeStamp?: number | string) => {
  const now = dayjs()
  const target = dayjs(timeStamp)
  const diff = now.diff(target, 'day')

  if (diff > 0) return diff + ' ' + t('old:daysAgo')
  return target.format('YYYY-MM-DD HH:mm')
}

/**
 * 多了未登入的 n 天前
 */
export const loginFromNow = (t: TranslateFunction, timeStamp?: number | string) => {
  if (!timeStamp) return t('old:unlogin')

  return fromNow(t, timeStamp)
}

/**
 * 解析日期字串為時間戳
 * @param dateStr - 日期字串（如 '2024-01-15' 或 '2024/01/15'）
 * @returns 時間戳（毫秒），無效則返回 undefined
 */
export const parseDate = (dateStr: string | undefined | null): number | undefined => {
  if (!dateStr) return undefined
  const date = new Date(dateStr)
  if (isNaN(date.getTime())) return undefined
  return date.getTime()
}
